import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import AIManagerService from 'src/services/AIManagerService';
import EventService from 'src/services/EventService';
import { StreamSection } from './StreamPageSections';
import type { ManagerAccess, ManagerProfile } from 'src/services/AIManagerService';
import type { Stream } from 'src/services/LeadStreamsService/agent';

export const managerStreamIds = (manager: ManagerProfile) =>
    Array.from(
        new Set((manager.lead_stream_ids ?? manager.intake_options?.selected.lead_stream_ids ?? []).map(Number))
    );

const sourceMode = (manager: ManagerProfile) =>
    manager.source_mode ?? manager.intake_options?.source_mode ?? 'packages';
export const streamManagerConnected = (manager: ManagerProfile, streamId: number) =>
    sourceMode(manager) !== 'subscription' && managerStreamIds(manager).includes(streamId);

export const streamConnectionPayload = (manager: ManagerProfile, streamId: number): Partial<ManagerProfile> => ({
    lead_stream_ids: Array.from(new Set([...managerStreamIds(manager), streamId])),
    ...(sourceMode(manager) === 'subscription' ? { source_mode: 'both' as const } : {}),
});

const allowedProfiles = (profiles: ManagerProfile[], access: ManagerAccess) => {
    const ids = access.profile_ids ?? (access.profile_id ? [access.profile_id] : []);
    return profiles.filter((profile) => ids.includes(profile.id));
};

export default function StreamManagerFunnel({ stream }: { stream: Stream }) {
    const history = useHistory();
    const [profiles, setProfiles] = useState<ManagerProfile[]>([]);
    const [access, setAccess] = useState<ManagerAccess | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedId, setSelectedId] = useState<number | ''>('');
    const [connecting, setConnecting] = useState(false);
    const [connectError, setConnectError] = useState('');
    const [notice, setNotice] = useState('');
    const mounted = useRef(false);
    const request = useRef(0);
    const actionLock = useRef(false);
    const streamRef = useRef(stream.id);
    streamRef.current = stream.id;

    const load = useCallback(async () => {
        const version = ++request.current;
        setLoading(true);
        setError('');
        try {
            const currentAccess = await AIManagerService.getAccess();
            const currentProfiles = currentAccess.state === 'granted' ? await AIManagerService.listProfiles() : [];
            if (!mounted.current || version !== request.current) return;
            setAccess(currentAccess);
            setProfiles(allowedProfiles(currentProfiles, currentAccess));
        } catch {
            if (mounted.current && version === request.current)
                setError('Не удалось загрузить менеджеров. Повторите попытку.');
        } finally {
            if (mounted.current && version === request.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        mounted.current = true;
        setDialogOpen(false);
        setSelectedId('');
        setNotice('');
        setProfiles([]);
        void load();
        return () => {
            mounted.current = false;
            ++request.current;
        };
    }, [stream.id, load]);

    const connected = profiles.filter((profile) => streamManagerConnected(profile, stream.id));
    const available = profiles.filter((profile) => !streamManagerConnected(profile, stream.id));
    const selectable = available.filter((profile) => profile.status !== 'running');
    const openManager = (id?: number) => history.push(id ? `/ai-manager?profile_id=${id}` : '/ai-manager');
    const openConnect = () => {
        setConnectError('');
        setSelectedId(selectable[0]?.id ?? '');
        setDialogOpen(true);
    };

    const connect = async () => {
        if (!selectedId || actionLock.current) return;
        const profileId = selectedId;
        const streamId = stream.id;
        actionLock.current = true;
        setConnecting(true);
        setConnectError('');
        try {
            // Re-read current sources before appending; never replace another profile's or stream's state.
            const currentAccess = await AIManagerService.getAccess(profileId);
            if (currentAccess.state !== 'granted') throw new Error('Доступ к менеджеру не подтверждён.');
            const currentProfiles = allowedProfiles(await AIManagerService.listProfiles(), currentAccess);
            const current = currentProfiles.find((profile) => profile.id === profileId);
            if (!current) throw new Error('Менеджер недоступен. Обновите список.');
            if (current.status === 'running') throw new Error('Сначала поставьте менеджера на паузу в его кабинете.');
            if (!current.intake_options?.package_streams.some((item) => item.id === streamId)) {
                throw new Error('Этот поток недоступен в источниках менеджера.');
            }
            if (!mounted.current || streamRef.current !== streamId) return;
            if (!streamManagerConnected(current, streamId)) {
                await AIManagerService.updateProfile(profileId, streamConnectionPayload(current, streamId));
            }
            if (!mounted.current || streamRef.current !== streamId) return;
            await load();
            if (!mounted.current || streamRef.current !== streamId) return;
            setDialogOpen(false);
            setNotice('Поток подключён. Проверьте настройки и тест в кабинете менеджера.');
            EventService.dispatch('AI_MANAGERS_UPDATED');
        } catch (cause: any) {
            if (!mounted.current || streamRef.current !== streamId) return;
            const data = cause?.response?.data;
            const validation = data?.lead_stream_ids || data?.source_mode || data?.detail;
            setConnectError(
                typeof validation === 'string'
                    ? validation
                    : Array.isArray(validation)
                    ? validation.join(' ')
                    : cause?.response
                    ? 'Не удалось подключить менеджера. Повторите попытку.'
                    : cause?.message || 'Не удалось подключить менеджера. Повторите попытку.'
            );
        } finally {
            actionLock.current = false;
            if (mounted.current) setConnecting(false);
        }
    };

    return (
        <StreamSection
            id="funnel"
            title="Воронка продаж"
        >
            {loading ? (
                <Box py={2}>
                    <CircularProgress
                        size={24}
                        aria-label="Загрузка менеджеров потока"
                    />
                </Box>
            ) : error ? (
                <Alert
                    severity="error"
                    action={<Button onClick={() => void load()}>Повторить</Button>}
                >
                    {error}
                </Alert>
            ) : access?.state !== 'granted' ? (
                <Stack
                    spacing={2}
                    alignItems="flex-start"
                >
                    <Typography color="text.secondary">
                        Доступ к ИИ-менеджерам можно запросить в их кабинете.
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={() => openManager()}
                    >
                        Открыть ИИ-менеджеров
                    </Button>
                </Stack>
            ) : (
                <Stack
                    spacing={2}
                    alignItems="stretch"
                >
                    {notice && <Alert severity="success">{notice}</Alert>}
                    {connected.length ? (
                        connected.map((profile) => (
                            <Stack
                                key={profile.id}
                                direction={{ xs: 'column', sm: 'row' }}
                                alignItems={{ xs: 'stretch', sm: 'center' }}
                                justifyContent="space-between"
                                gap={1.5}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={1}
                                    flexWrap="wrap"
                                >
                                    <Typography fontWeight={600}>{profile.name}</Typography>
                                    <Chip
                                        size="small"
                                        label={profile.status_label || profile.status}
                                    />
                                </Stack>
                                <Button
                                    variant="outlined"
                                    onClick={() => openManager(profile.id)}
                                >
                                    Открыть менеджера
                                </Button>
                            </Stack>
                        ))
                    ) : (
                        <Typography color="text.secondary">
                            Выберите менеджера, который будет работать с лидами этого потока.
                        </Typography>
                    )}
                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        Подключение добавляет поток в источники. Тест, Telegram и запуск настраиваются в кабинете
                        менеджера.
                    </Typography>
                    <Box>
                        {available.length ? (
                            <Button
                                variant={connected.length ? 'outlined' : 'contained'}
                                onClick={openConnect}
                            >
                                {connected.length ? 'Подключить ещё менеджера' : 'Подключить ИИ-менеджера'}
                            </Button>
                        ) : !profiles.length ? (
                            <Button
                                variant="contained"
                                onClick={() => openManager()}
                            >
                                Создать менеджера
                            </Button>
                        ) : null}
                    </Box>
                </Stack>
            )}
            <Dialog
                open={dialogOpen}
                onClose={connecting ? undefined : () => setDialogOpen(false)}
                fullWidth
                maxWidth="sm"
                aria-labelledby="stream-manager-connect-title"
                PaperProps={{ sx: { bgcolor: 'var(--lm-surface)', borderRadius: '20px', p: 1 } }}
            >
                <DialogTitle
                    id="stream-manager-connect-title"
                    sx={{ fontSize: 22, fontWeight: 700 }}
                >
                    Подключить ИИ-менеджера
                </DialogTitle>
                <DialogContent>
                    <Stack
                        spacing={2}
                        pt={1}
                    >
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Текущие источники менеджера сохранятся. Подключение не запускает его автоматически.
                        </Typography>
                        {connectError && <Alert severity="error">{connectError}</Alert>}
                        <TextField
                            select
                            fullWidth
                            label="ИИ-менеджер"
                            value={selectedId}
                            onChange={(event) => setSelectedId(Number(event.target.value))}
                            disabled={connecting}
                        >
                            {available.map((profile) => (
                                <MenuItem
                                    key={profile.id}
                                    value={profile.id}
                                    disabled={profile.status === 'running'}
                                >
                                    {profile.name}
                                    {profile.status === 'running' ? ' · работает' : ''}
                                </MenuItem>
                            ))}
                        </TextField>
                        {available.some((profile) => profile.status === 'running') && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Чтобы изменить источники работающего менеджера, сначала поставьте его на паузу в
                                кабинете.
                            </Typography>
                        )}
                        {!selectable.length && <Button onClick={() => openManager()}>Открыть ИИ-менеджеров</Button>}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => setDialogOpen(false)}
                        disabled={connecting}
                    >
                        Отмена
                    </Button>
                    <Button
                        variant="contained"
                        disabled={connecting || !selectedId}
                        onClick={() => void connect()}
                    >
                        {connecting ? 'Подключаем…' : 'Подключить'}
                    </Button>
                </DialogActions>
            </Dialog>
        </StreamSection>
    );
}
